// The 3E Leak Finder — survey definition (50 questions across 9 sections)
// types: text | email | textarea | select | rating

export const SURVEY_SECTIONS = [
  {
    title: "Business Basics",
    subtitle: "Tell us about you and your business.",
    questions: [
      { id: "q1", label: "What is your name?", type: "text", required: true },
      { id: "q2", label: "What is your email address?", type: "email", required: true },
      { id: "q3", label: "What is your business or brand name?", type: "text", required: true },
      { id: "q4", label: "What type of business do you run?", type: "text" },
      { id: "q5", label: "What city / state are you based in?", type: "text" },
      { id: "q6", label: "What is your website or main social media link?", type: "text" },
      { id: "q7", label: "What do you sell?", type: "textarea" },
      { id: "q8", label: "Who is your ideal customer?", type: "textarea" },
      { id: "q9", label: "What is your main business goal right now?", type: "textarea" },
      { id: "q10", label: "What is the biggest problem you are trying to solve?", type: "textarea" },
    ],
  },
  {
    title: "Brand Clarity",
    subtitle: "Can people instantly understand what you do?",
    questions: [
      { id: "q11", label: "In one sentence, what does your business do?", type: "textarea" },
      { id: "q12", label: "Can a stranger understand what you sell within 5 seconds of seeing your page or profile?", type: "select", options: ["Yes", "Somewhat", "No"] },
      { id: "q13", label: "Do your visuals, colors, photos, and content all feel like the same brand?", type: "select", options: ["Yes", "Somewhat", "No"] },
      { id: "q14", label: "What do people most often misunderstand about your business?", type: "textarea" },
      { id: "q15", label: "Rate your brand clarity", type: "rating" },
    ],
  },
  {
    title: "Offer Clarity",
    subtitle: "Is your offer clear and easy to buy?",
    questions: [
      { id: "q16", label: "What is your main offer or service?", type: "textarea" },
      { id: "q17", label: "Is the price clearly listed or easy to understand?", type: "select", options: ["Yes", "Somewhat", "No"] },
      { id: "q18", label: "Is it clear what the customer gets after they pay?", type: "select", options: ["Yes", "Somewhat", "No"] },
      { id: "q19", label: "Do you have too many offers, unclear offers, or outdated offers?", type: "select", options: ["Yes", "No", "Not sure"] },
      { id: "q20", label: "Rate your offer clarity", type: "rating" },
    ],
  },
  {
    title: "Marketing & Visibility",
    subtitle: "How consistently do you get in front of people?",
    questions: [
      { id: "q21", label: "Where do most people find you?", type: "select", options: ["Instagram", "Facebook", "TikTok", "Google", "Word of mouth", "Events", "Referrals", "Other"] },
      { id: "q22", label: "How often do you post or promote your business?", type: "select", options: ["Daily", "A few times a week", "Weekly", "Rarely", "Almost never"] },
      { id: "q23", label: "Does your content clearly tell people what to do next?", type: "select", options: ["Yes", "Sometimes", "No"] },
      { id: "q24", label: "Are you getting attention but not enough sales?", type: "select", options: ["Yes", "No", "Not sure"] },
      { id: "q25", label: "Rate your marketing consistency", type: "rating" },
    ],
  },
  {
    title: "Customer Journey",
    subtitle: "How easy is it to take the next step with you?",
    questions: [
      { id: "q26", label: "What action do you want customers to take?", type: "select", options: ["Buy", "Book a call", "Fill out a form", "Visit location", "Follow", "Subscribe", "Other"] },
      { id: "q27", label: "Is that action easy to find on your website or page?", type: "select", options: ["Yes", "Somewhat", "No"] },
      { id: "q28", label: "How many clicks does it take for someone to buy, book, or contact you?", type: "select", options: ["1", "2", "3", "4+", "Not sure"] },
      { id: "q29", label: "Do you have a thank-you page, confirmation email, or next-step message?", type: "select", options: ["Yes", "No", "Not sure"] },
      { id: "q30", label: "Rate your customer journey", type: "rating" },
    ],
  },
  {
    title: "Sales & Conversion",
    subtitle: "How well does interest turn into sales?",
    questions: [
      { id: "q31", label: "How many leads or inquiries do you get per week?", type: "text" },
      { id: "q32", label: "How many of those usually become paying customers?", type: "text" },
      { id: "q33", label: "What stops people from buying?", type: "select", options: ["Price", "Confusion", "No urgency", "Trust", "Timing", "They disappear", "Not sure"] },
      { id: "q34", label: "Do you follow up with people who show interest but do not buy?", type: "select", options: ["Yes", "Sometimes", "No"] },
      { id: "q35", label: "Rate your conversion system", type: "rating" },
    ],
  },
  {
    title: "Pricing & Revenue",
    subtitle: "Are you capturing the revenue you deserve?",
    questions: [
      { id: "q36", label: "What is your average sale amount?", type: "text" },
      { id: "q37", label: "Do you feel your prices are too low, too high, or unclear?", type: "select", options: ["Too low", "Too high", "Unclear", "Fair"] },
      { id: "q38", label: "Do you have packages, bundles, or upsells?", type: "select", options: ["Yes", "No", "Not sure"] },
      { id: "q39", label: "Are you leaving money on the table because customers do not know everything you offer?", type: "select", options: ["Yes", "No", "Not sure"] },
      { id: "q40", label: "Rate your pricing / revenue structure", type: "rating" },
    ],
  },
  {
    title: "Operations & Execution",
    subtitle: "Is your business organized to deliver consistently?",
    questions: [
      { id: "q41", label: "What task takes up too much of your time?", type: "textarea" },
      { id: "q42", label: "What part of your business feels most unorganized?", type: "select", options: ["Marketing", "Sales", "Inventory", "Scheduling", "Follow-up", "Customer service", "Money tracking", "Content", "Other"] },
      { id: "q43", label: "Do you have repeatable systems, checklists, or templates?", type: "select", options: ["Yes", "Some", "No"] },
      { id: "q44", label: "What do you know you should be doing but are not doing consistently?", type: "textarea" },
      { id: "q45", label: "Rate your execution", type: "rating" },
    ],
  },
  {
    title: "Final Diagnosis",
    subtitle: "Let's pinpoint the biggest leak.",
    questions: [
      { id: "q46", label: "What do you think is the biggest leak in your business right now?", type: "textarea" },
      { id: "q47", label: "If you could fix one thing in the next 30 days, what would it be?", type: "textarea" },
      { id: "q48", label: "What would make this audit / results most useful for you?", type: "textarea" },
      { id: "q49", label: "Are you interested in help fixing the leaks found in this survey?", type: "select", options: ["Yes, I want help", "Maybe", "No, just send results"] },
      { id: "q50", label: "Would you like to book a follow-up strategy call?", type: "select", options: ["Yes", "Maybe later", "No"] },
    ],
  },
];

export const TOTAL_QUESTIONS = SURVEY_SECTIONS.reduce((n, s) => n + s.questions.length, 0);
